import type { Camera2D, Viewport } from '../hex/camera2d';
import type { Point, Rect, Tile } from '../hex/types';
import { axialToPixel, hexVertices } from '../hex/hexMath';
import type { MapModel } from '../map/mapModel';
import type { Settlement } from '../map/settlement';
import type { FoundingExpedition } from '../expedition/founding';
import type { TransportRoute } from '../transport/route';
import type { ScoutingMission } from '../scouting/scouting';
import type { BattleReport } from '../battle/battleEngine';
import type { EnemyForce } from '../battle/encounters';
import type { AbstractModal } from '../../modal/AbstractModal';
import type { GameConfig, ConfigError } from '../../config/gameConfig';
import type { IconRegistry } from '../../ui/overlay/assets/IconRegistry';
import type { SoundManager } from '../../audio/SoundManager';
import type { TutorialState } from '../../tutorials/runtime';
import { renderHex } from './hex/renderHex';
import { drawModals } from './drawModals';
import { drawOverlay } from './drawOverlay';
import { drawTooltip } from './drawTooltip';
import { isMobileViewport } from './modalLayout';
import { drawTutorialOverlay } from './drawTutorialOverlay';

export type ModalAction = {
    id: string;
    rect: Rect;
    enabled: boolean;
};

export type ActionRect = {
    id: string;
    rect: Rect;
    enabled: boolean;
};

export type SimulationState = {
    running: boolean;
    speed: number;
    accumulator: number;
    lastTime: number;
    tick: number;
};

export type HexMapState = {
    pointer: Point;
    dragging: boolean;
    dragStart: Point;
    camStart: Point;
    hover: Tile | null;
    ownedTileIds: Set<string>;
    discoveredTileIds: Set<string>;
    settlementsById: Record<string, Settlement>;
    settlementByTileId: Record<string, Settlement>;
    foundingExpeditions: FoundingExpedition[];
    expeditionIdCounter: number;
    transportRoutes: TransportRoute[];
    transportIdCounter: number;
    scoutingMissions: ScoutingMission[];
    scoutingIdCounter: number;
    battleReports: BattleReport[];
    battleReportIdCounter: number;
    scoutPlanningMissionId: string | null;
    enemyForces: Record<string, EnemyForce>;
    dismissedNotifications: Set<string>;
    alerts: Array<{ id: string; message: string; level: 'warn' | 'error' }>;
    alertIdCounter: number;
    overlay: {
        showNotifications: boolean;
        speedMenuOpen: boolean;
        languageMenuOpen: boolean;
    };
    config: GameConfig;
    settingsEditor: {
        text: string;
        error: ConfigError | null;
    };
    modals: AbstractModal[];
    activeModalId: string | null;
    modalDragId: string | null;
    modalDragOffset: Point;
    isModalHover: boolean;
    actionBarActions: ActionRect[];
    overlayAssets: {
        icons: IconRegistry;
    };
    audio: {
        manager: SoundManager;
    };
    tutorial: TutorialState | null;
    simulation: SimulationState;
    dirty: boolean;
};

export function drawFrame(
    ctx: CanvasRenderingContext2D,
    model: MapModel,
    camera: Camera2D,
    state: HexMapState,
    viewport: Viewport,
): void {
    const neighborDirs = [
        { q: 1, r: 0, s: -1 },
        { q: 1, r: -1, s: 0 },
        { q: 0, r: -1, s: 1 },
        { q: -1, r: 0, s: 1 },
        { q: -1, r: 1, s: 0 },
        { q: 0, r: 1, s: -1 },
    ];

    ctx.clearRect(0, 0, viewport.width, viewport.height);
    ctx.save();
    ctx.translate(viewport.width / 2, viewport.height / 2);
    ctx.scale(camera.zoom, camera.zoom);
    ctx.translate(camera.offsetX, camera.offsetY);

    for (const tile of model.tiles) {
        const axial = { q: tile.position.q, r: tile.position.r };
        const center = axialToPixel(axial);
        const verts = hexVertices(center);
        const settlement = state.settlementByTileId[tile.id];
        const neighbors = neighborDirs
            .map((dir) => {
                const nq = tile.position.q + dir.q;
                const nr = tile.position.r + dir.r;
                const ns = tile.position.s + dir.s;
                const neighbor = model.getTileByCube(nq, nr, ns);
                if (!neighbor) return null;
                return {
                    tile: neighbor,
                    center: axialToPixel({ q: neighbor.position.q, r: neighbor.position.r }),
                };
            })
            .filter((entry): entry is { tile: Tile; center: Point } => entry !== null);
        renderHex({
            ctx,
            tile,
            center,
            verts,
            zoom: camera.zoom,
            state,
            settlement: settlement || null,
            neighbors,
        });
    }

    if (state.modals.length > 0) {
        for (const modal of state.modals) {
            if (!modal.showTileHighlight) continue;
            const center = axialToPixel({ q: modal.tile.position.q, r: modal.tile.position.r });
            const verts = hexVertices(center);
            ctx.beginPath();
            ctx.moveTo(verts[0].x, verts[0].y);
            for (let i = 1; i < verts.length; i += 1) ctx.lineTo(verts[i].x, verts[i].y);
            ctx.closePath();
            ctx.strokeStyle = modal.id === state.activeModalId ? '#f4d35e' : '#d6b24a';
            ctx.lineWidth = (modal.id === state.activeModalId ? 2 : 1.5) / camera.zoom;
            ctx.stroke();
        }
    }

    ctx.restore();

    if (!(isMobileViewport(viewport) && state.modals.length > 0)) {
        drawTooltip(ctx, state, viewport);
    }
    drawModals(ctx, state, viewport);
    drawOverlay(ctx, model, state, viewport, camera.zoom);
    drawTutorialOverlay(ctx, state, model, camera, viewport);
}
